import { createEffect, createEvent, createStore, sample } from 'effector';
import { subscribeEventBus } from '../services/assistantClient';

/**
 * Assistant SSE session lifecycle.
 *
 * Lives in its own module so that both `assistantHud.ts` (POST flows) and
 * `sceneOpen.ts` (scene_open POST flow) can read `$assistantSessionId`
 * without a circular import.
 *
 * SessionId is intentionally **not** reset on `assistantChatReset`.
 * Reasons:
 *  - The SSE stream (`connectAssistantStreamFx`) is bound to the sessionId
 *    that was active at the time of subscription. Resetting the store would
 *    leave the stream subscribed to the old session while subsequent POSTs
 *    (e.g. scene_open after returning to map) go out with `sessionId:
 *    undefined`, the backend creates a NEW session, and the SSE reply lands
 *    on a session nobody is listening to.
 *  - Treating sessionId as stable across `resetNavigation()` matches backend
 *    expectations: the server can reset chat history server-side when the
 *    client signals "back to map" (out of scope here); the transport-layer
 *    session id stays the same for the tab lifetime.
 */

const createSessionId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const initialSessionId = createSessionId();

export const assistantStreamSessionReceived = createEvent<string>();

const normalizeSessionId = (sessionId: string | null | undefined): string | null => {
  const trimmed = sessionId?.trim() ?? '';
  return trimmed ? trimmed : null;
};

export const $assistantSessionId = createStore<string | null>(initialSessionId).on(
  assistantStreamSessionReceived,
  (_, sessionId) => normalizeSessionId(sessionId),
);

// --- SSE stream ---

export const assistantStreamMessageReceived = createEvent<
  import('../shared/eventBus').EventBusMessage
>();

/**
 * Fires from `subscribeEventBus.onConnected` (HTTP-level signal — the
 * `fetch()` for `/api/stream?sessionId=...` has resolved with ok status
 * and a live ReadableStream). This is the reliable "we are subscribed"
 * signal: it does NOT depend on the backend emitting any particular
 * payload type first (some backends never emit the `{sessionId}`
 * handshake-only packet — they go straight to `event`-bearing
 * messages, so relying on `onSession` would deadlock the gate when
 * scene_open is the first POST and backend only replies in response
 * to our POSTs).
 */
export const assistantStreamHandshakeReceived = createEvent();

/**
 * True once the SSE stream has actually delivered a real network packet
 * (either the handshake `{sessionId}` message or any `event` message).
 * Used as a gate for `scene_open` POSTs:
 *
 *   Without the gate, a fast `scene_open` (no OpenAI, no thinking) can
 *   produce an `assistant_message` on the backend BEFORE our SSE
 *   `fetch()` for `/api/stream?sessionId=...` completes its handshake.
 *   The reply lands in a stream nobody is reading and the intro card
 *   silently disappears.
 *
 * **Crucially NOT** wired to `assistantStreamSessionReceived` — that
 * event is also dispatched locally on module load + from POST response
 * `doneData.sessionId`. Subscribing `ready` to it would flip `ready=true`
 * synchronously during module init, before any network connection
 * actually exists — defeating the purpose of the gate.
 *
 * NOT reset on SSE disconnect — `connectAssistantStreamFx` runs an
 * internal reconnect loop, and on every reconnect the backend re-sends
 * the session handshake which keeps this `true`. The flag therefore
 * holds "stream worked at least once" semantics; combined with the
 * timeout safety-net in `sceneOpen.ts`, this is sufficient for the
 * observed race. If we ever need strict "is stream connected RIGHT NOW",
 * the only correct path is to instrument `subscribeEventBus` itself
 * with onConnected / onDisconnect callbacks.
 */
export const $assistantStreamReady = createStore(false)
  .on(assistantStreamHandshakeReceived, () => true)
  .on(assistantStreamMessageReceived, () => true);

let activeStreamSessionId: string | null = null;
let activeStreamController: AbortController | null = null;

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    const timeout = setTimeout(resolve, ms);
    if (signal) {
      signal.addEventListener(
        'abort',
        () => {
          clearTimeout(timeout);
          resolve();
        },
        { once: true },
      );
    }
  });

const getReconnectDelayMs = (attempt: number) => {
  const base = 1000;
  const max = 20000;
  const exp = Math.min(max, base * Math.pow(2, attempt));
  const jitter = Math.floor(Math.random() * 300);
  return exp + jitter;
};

const connectAssistantStreamFx = createEffect<{ sessionId: string }, void>(
  async ({ sessionId }) => {
    if (!sessionId) {
      return;
    }
    if (activeStreamSessionId === sessionId) {
      return;
    }
    if (activeStreamController) {
      activeStreamController.abort();
    }
    const controller = new AbortController();
    activeStreamSessionId = sessionId;
    activeStreamController = controller;

    let attempt = 0;
    while (!controller.signal.aborted) {
      try {
        await subscribeEventBus(
          sessionId,
          {
            // HTTP-level "we're subscribed" — fires synchronously right
            // after fetch resolves with response.ok. This is what flips
            // `$assistantStreamReady` → flushes any queued `scene_open`
            // POSTs. Independent of backend ever sending the optional
            // `{sessionId}` handshake-only payload (some don't, they go
            // straight to `event`-bearing messages — relying on
            // `onSession` alone would deadlock on a backend that only
            // replies in response to POSTs).
            onConnected: () => assistantStreamHandshakeReceived(),
            onMessage: (message) => assistantStreamMessageReceived(message),
            // Best-effort session-id refresh from backend if it does
            // emit the `{sessionId}` packet. The gate is driven by
            // `onConnected`, not by this.
            onSession: (nextSessionId) => assistantStreamSessionReceived(nextSessionId),
            onError: (error) => {
              if (typeof window !== 'undefined') {
                console.warn('event-bus: stream error', error);
              }
            },
          },
          controller.signal,
        );
        attempt = 0;
      } catch (error) {
        if (typeof window !== 'undefined') {
          console.warn('event-bus: stream disconnected', error);
        }
      }

      if (controller.signal.aborted) {
        break;
      }
      const delayMs = getReconnectDelayMs(attempt);
      attempt += 1;
      await sleep(delayMs, controller.signal);
    }
  },
);

sample({
  clock: assistantStreamSessionReceived,
  filter: (sessionId) => Boolean(sessionId.trim()),
  fn: (sessionId) => ({ sessionId }),
  target: connectAssistantStreamFx,
});

if (initialSessionId) {
  assistantStreamSessionReceived(initialSessionId);
}

sample({
  clock: assistantStreamMessageReceived,
  fn: (message) => message.sessionId,
  target: assistantStreamSessionReceived,
});
